import {
  EditTemplate,
  ReverseProvisioningTemplate,
} from '@agilelab/plugin-wb-builder-common';
import { tooManyEditTemplatesError } from '@agilelab/plugin-wb-platform-common';
import { Entity } from '@mainstage/catalog-model';
import { useApi, useRouteRef } from '@mainstage/core-plugin-api';
import { catalogApiRef, entityRouteRef } from '@mainstage/plugin-catalog-react';
import { useState } from 'react';
import useAsync from 'react-use/lib/useAsync';

export type CustomTemplate = {
  ref: string;
  kind: string;
  apiVersion: string;
};

/**
 * links, for every template, an array of custom templates (ReverseProvisioningTemplate/EditTemplate)
 * in order for the UI to render, for every template in the Template page list,
 * a custom icon (magic wand/edit icons) if that template has a connection with one or more custom templates.
 * This is called only once when template page list is rendered.
 * @param entities of kind Template
 */
export const useWitboostCustomTemplates = (entities: Entity[]) => {
  const createEntityRoute = useRouteRef(entityRouteRef);
  const catalogApi = useApi(catalogApiRef);

  const [associatedTemplatesState, setAssociatedTemplatesState] = useState(
    {} as Record<string, CustomTemplate[]>,
  );

  useAsync(async () => {
    if (!entities) return;
    const { items } = await catalogApi.getEntities({
      filter: {
        kind: ['ReverseProvisioningTemplate', 'EditTemplate'],
      },
    });
    // {'useCaseTemplateId' => [ {'kind': 'EditTemplate', 'ref': 'entityPageUrl'}, ... ] }

    const mapOfCustomTemplate: Record<string, CustomTemplate[]> = items.reduce(
      (acc, item) => {
        const castItem = item as ReverseProvisioningTemplate | EditTemplate;
        //
        if (castItem.spec.useCaseTemplateId) {
          castItem.spec.useCaseTemplateId.forEach(templateId => {
            const lowerTemplateId = templateId.toLowerCase();
            const customTemplatePageUrl = createEntityRoute({
              name: castItem.metadata.name,
              kind: castItem.kind.toLocaleLowerCase(),
              namespace: castItem.metadata.namespace ?? 'default',
            });

            if (acc[lowerTemplateId])
              acc[lowerTemplateId].push({
                kind: castItem.kind.toLocaleLowerCase(),
                ref: customTemplatePageUrl,
                apiVersion: castItem.apiVersion,
              });
            else
              acc[lowerTemplateId] = [
                {
                  kind: castItem.kind.toLocaleLowerCase(),
                  ref: customTemplatePageUrl,
                  apiVersion: castItem.apiVersion,
                },
              ];
          });
        }
        return acc;
      },
      {} as Record<string, CustomTemplate[]>,
    );

    setAssociatedTemplatesState(mapOfCustomTemplate);
  }, [entities]);

  return associatedTemplatesState;
};

/**
 * Finds duplicate custom templates of a specific kind.
 *
 * @param {CustomTemplate[]} customTemplates - An array of custom templates to search for duplicates.
 * @param {'edittemplate' | 'reverseprovisioningtemplate'} kind - The kind of custom template to find duplicates of.
 *
 * - `error` is a boolean indicating whether an error occurred.
 * - `result` is the found custom template or `undefined` if not found or in case of an error.
 * - `errorMessage` is an optional string providing an error message if an error occurred.
 */
export function findDuplicatesCustomTemplates(
  kind: 'edittemplate' | 'reverseprovisioningtemplate',
  customTemplates?: CustomTemplate[],
): {
  error: boolean;
  result: CustomTemplate | undefined;
  errorMessage?: string;
} {
  const filtered = customTemplates?.filter(
    customTemplate => customTemplate.kind === kind,
  );
  if (!filtered) return { error: false, result: undefined };
  if (filtered.length === 1) return { error: false, result: filtered.at(0) };
  else if (filtered.length === 0) return { error: false, result: undefined };

  const foundTemplateVersions = new Set<string>();
  let errorMessage: string | undefined;

  for (const templateEntity of filtered) {
    if (foundTemplateVersions.has(templateEntity.apiVersion)) {
      errorMessage = tooManyEditTemplatesError(templateEntity.apiVersion);
      break;
    }
    foundTemplateVersions.add(templateEntity.apiVersion);
  }

  if (errorMessage) return { error: true, result: undefined, errorMessage };

  return {
    error: false,
    result: filtered.at(0),
  };
}
